import { supabase } from './supabase';
import { logAuthEvent } from './auditLogger';
import CryptoJS from 'crypto-js';

// Types
export interface SecureDocument {
  id: string;
  name: string;
  description?: string;
  fileType: string;
  fileSize: number;
  isShared: boolean;
  createdAt: Date;
  updatedAt: Date;
  lastAccessedAt?: Date;
  version: number;
  tags: string[];
  metadata: Record<string, any>;
}

export interface DocumentPermission {
  id: string;
  documentId: string;
  grantedBy: string;
  grantedTo: string;
  permissionLevel: 'read' | 'write' | 'admin';
  grantedAt: Date;
  expiresAt?: Date;
  revokedAt?: Date;
  revokedBy?: string;
  isActive: boolean;
}

export interface DocumentAccessLog {
  documentId: string;
  accessType: 'view' | 'download' | 'edit' | 'print' | 'share';
  accessStatus: 'success' | 'denied' | 'error';
  additionalInfo?: Record<string, any>;
}

// Constants
const ENCRYPTION_ALGORITHM = 'AES-256';
const MAX_UPLOAD_SIZE = 100 * 1024 * 1024; // 100MB
const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
  'text/csv',
  'image/jpeg',
  'image/png',
  'image/gif'
];

// Helper function to generate a secure encryption key
export const generateEncryptionKey = (): { key: string; iv: string } => {
  const key = CryptoJS.lib.WordArray.random(32).toString(); // 256 bits
  const iv = CryptoJS.lib.WordArray.random(16).toString(); // 128 bits
  return { key, iv };
};

// Encrypt file data
export const encryptFileData = async (
  fileData: ArrayBuffer,
  key: string,
  iv: string
): Promise<string> => {
  // Convert ArrayBuffer to WordArray
  const wordArray = CryptoJS.lib.WordArray.create(fileData);
  
  // Encrypt using AES
  const encrypted = CryptoJS.AES.encrypt(wordArray, key, {
    iv: CryptoJS.enc.Hex.parse(iv),
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7
  });
  
  return encrypted.toString();
};

// Decrypt file data
export const decryptFileData = async (
  encryptedData: string,
  key: string,
  iv: string
): Promise<ArrayBuffer> => {
  // Decrypt using AES
  const decrypted = CryptoJS.AES.decrypt(encryptedData, key, {
    iv: CryptoJS.enc.Hex.parse(iv),
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7
  });
  
  // Convert WordArray to ArrayBuffer
  const words = decrypted.words;
  const sigBytes = decrypted.sigBytes;
  const u8 = new Uint8Array(sigBytes);
  
  for (let i = 0; i < sigBytes; i++) {
    const byte = (words[i >>> 2] >>> (24 - (i % 4) * 8)) & 0xff;
    u8[i] = byte;
  }
  
  return u8.buffer;
};

// Calculate hash of file data
export const calculateFileHash = async (fileData: ArrayBuffer): Promise<string> => {
  const wordArray = CryptoJS.lib.WordArray.create(fileData);
  return CryptoJS.SHA256(wordArray).toString();
};

// Validate file type and size
export const validateFile = (file: File): { valid: boolean; error?: string } => {
  if (!file) {
    return { valid: false, error: 'No file provided' };
  }
  
  if (file.size > MAX_UPLOAD_SIZE) {
    return { 
      valid: false, 
      error: `File size exceeds maximum allowed size of ${MAX_UPLOAD_SIZE / (1024 * 1024)}MB` 
    };
  }
  
  if (!ALLOWED_FILE_TYPES.includes(file.type)) {
    return { 
      valid: false, 
      error: 'File type not allowed. Allowed types: PDF, Word, Excel, Text, CSV, JPEG, PNG, GIF' 
    };
  }
  
  return { valid: true };
};

// Sanitize file name to prevent path traversal and XSS
export const sanitizeFileName = (fileName: string): string => {
  // Remove path components
  let sanitized = fileName.replace(/^.*[\\\/]/, '');
  
  // Remove special characters
  sanitized = sanitized.replace(/[^\w\s.-]/g, '_');
  
  // Limit length
  if (sanitized.length > 255) {
    const ext = sanitized.split('.').pop() || '';
    sanitized = sanitized.substring(0, 250 - ext.length) + '.' + ext;
  }
  
  return sanitized;
};

// Upload a secure document
export const uploadSecureDocument = async (
  file: File,
  name: string,
  description?: string,
  tags: string[] = [],
  metadata: Record<string, any> = {}
): Promise<{ success: boolean; documentId?: string; error?: string }> => {
  try {
    // Validate file
    const validation = validateFile(file);
    if (!validation.valid) {
      return { success: false, error: validation.error };
    }
    
    // Sanitize inputs
    const sanitizedName = sanitizeFileName(name || file.name);
    const sanitizedTags = tags.map(tag => tag.trim()).filter(Boolean);
    
    // Generate encryption key and IV
    const { key, iv } = generateEncryptionKey();
    
    // Read file as ArrayBuffer
    const fileData = await file.arrayBuffer();
    
    // Calculate original file hash (before encryption)
    const originalHash = await calculateFileHash(fileData);
    
    // Encrypt file data
    const encryptedData = await encryptFileData(fileData, key, iv);
    
    // Calculate hash of encrypted data
    const contentHash = CryptoJS.SHA256(encryptedData).toString();
    
    // Generate storage path
    const storagePath = `secure_documents/${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    
    // Upload encrypted file to storage
    const { error: uploadError } = await supabase.storage
      .from('secure_files')
      .upload(storagePath, encryptedData, {
        contentType: 'application/octet-stream', // Always use generic type for encrypted data
        cacheControl: 'private, max-age=0, no-cache'
      });
      
    if (uploadError) {
      throw new Error(`Storage upload failed: ${uploadError.message}`);
    }
    
    // Create document record in database
    const { data, error } = await supabase
      .from('secure_documents')
      .insert({
        name: sanitizedName,
        description,
        content_hash: contentHash,
        encryption_metadata: {
          algorithm: ENCRYPTION_ALGORITHM,
          iv,
          originalHash
        },
        file_type: file.type,
        file_size: file.size,
        storage_path: storagePath,
        tags: sanitizedTags,
        metadata: {
          ...metadata,
          originalName: file.name,
          encryptedSize: encryptedData.length
        }
      })
      .select('id')
      .single();
      
    if (error) {
      // Clean up storage if database insert fails
      await supabase.storage
        .from('secure_files')
        .remove([storagePath]);
        
      throw new Error(`Database insert failed: ${error.message}`);
    }
    
    // Store encryption key securely in localStorage
    // In a production app, consider more secure key management
    localStorage.setItem(`secure_doc_key_${data.id}`, JSON.stringify({ key, iv }));
    
    // Log successful upload
    await logDocumentAccess(data.id, 'upload', 'success', {
      fileName: sanitizedName,
      fileType: file.type,
      fileSize: file.size
    });
    
    return { success: true, documentId: data.id };
    
  } catch (error: any) {
    console.error('Secure document upload failed:', error);
    
    // Log failed upload attempt
    await logAuthEvent('document', 'upload', {
      status: 'failed',
      error: error.message
    });
    
    return { success: false, error: error.message };
  }
};

// Get documents for current user (owned and shared)
export const getSecureDocuments = async (): Promise<{ 
  owned: SecureDocument[]; 
  shared: SecureDocument[];
  error?: string;
}> => {
  try {
    // Get owned documents
    const { data: ownedData, error: ownedError } = await supabase
      .from('secure_documents')
      .select('*')
      .order('created_at', { ascending: false });
      
    if (ownedError) {
      throw new Error(`Failed to fetch owned documents: ${ownedError.message}`);
    }
    
    // Get shared documents
    const { data: permissionsData, error: permissionsError } = await supabase
      .from('document_permissions')
      .select(`
        id,
        document_id,
        permission_level,
        granted_at,
        expires_at,
        secure_documents:document_id (*)
      `)
      .eq('is_active', true);
      
    if (permissionsError) {
      throw new Error(`Failed to fetch shared documents: ${permissionsError.message}`);
    }
    
    // Transform data
    const owned = (ownedData || []).map(doc => ({
      id: doc.id,
      name: doc.name,
      description: doc.description,
      fileType: doc.file_type,
      fileSize: doc.file_size,
      isShared: doc.is_shared,
      createdAt: new Date(doc.created_at),
      updatedAt: new Date(doc.updated_at),
      lastAccessedAt: doc.last_accessed_at ? new Date(doc.last_accessed_at) : undefined,
      version: doc.version,
      tags: doc.tags,
      metadata: doc.metadata
    }));
    
    const shared = (permissionsData || [])
      .filter(perm => perm.secure_documents)
      .map(perm => ({
        id: perm.secure_documents.id,
        name: perm.secure_documents.name,
        description: perm.secure_documents.description,
        fileType: perm.secure_documents.file_type,
        fileSize: perm.secure_documents.file_size,
        isShared: true,
        createdAt: new Date(perm.secure_documents.created_at),
        updatedAt: new Date(perm.secure_documents.updated_at),
        lastAccessedAt: perm.secure_documents.last_accessed_at 
          ? new Date(perm.secure_documents.last_accessed_at) 
          : undefined,
        version: perm.secure_documents.version,
        tags: perm.secure_documents.tags,
        metadata: {
          ...perm.secure_documents.metadata,
          permissionLevel: perm.permission_level,
          grantedAt: perm.granted_at,
          expiresAt: perm.expires_at
        }
      }));
    
    return { owned, shared };
    
  } catch (error: any) {
    console.error('Failed to fetch secure documents:', error);
    return { owned: [], shared: [], error: error.message };
  }
};

// Get a single document by ID
export const getSecureDocument = async (documentId: string): Promise<{
  document?: SecureDocument;
  permissionLevel?: 'owner' | 'read' | 'write' | 'admin';
  error?: string;
}> => {
  try {
    // Get document
    const { data: document, error: documentError } = await supabase
      .from('secure_documents')
      .select('*')
      .eq('id', documentId)
      .single();
      
    if (documentError) {
      if (documentError.code === 'PGRST116') {
        // Document not found, check if it's shared with user
        const { data: permission, error: permissionError } = await supabase
          .from('document_permissions')
          .select(`
            permission_level,
            secure_documents:document_id (*)
          `)
          .eq('document_id', documentId)
          .eq('is_active', true)
          .single();
          
        if (permissionError) {
          throw new Error('Document not found or you do not have permission to access it');
        }
        
        // Log successful access
        await logDocumentAccess(documentId, 'view', 'success');
        
        return {
          document: {
            id: permission.secure_documents.id,
            name: permission.secure_documents.name,
            description: permission.secure_documents.description,
            fileType: permission.secure_documents.file_type,
            fileSize: permission.secure_documents.file_size,
            isShared: true,
            createdAt: new Date(permission.secure_documents.created_at),
            updatedAt: new Date(permission.secure_documents.updated_at),
            lastAccessedAt: permission.secure_documents.last_accessed_at 
              ? new Date(permission.secure_documents.last_accessed_at) 
              : undefined,
            version: permission.secure_documents.version,
            tags: permission.secure_documents.tags,
            metadata: permission.secure_documents.metadata
          },
          permissionLevel: permission.permission_level as 'read' | 'write' | 'admin'
        };
      }
      
      throw new Error(`Failed to fetch document: ${documentError.message}`);
    }
    
    // Log successful access
    await logDocumentAccess(documentId, 'view', 'success');
    
    return {
      document: {
        id: document.id,
        name: document.name,
        description: document.description,
        fileType: document.file_type,
        fileSize: document.file_size,
        isShared: document.is_shared,
        createdAt: new Date(document.created_at),
        updatedAt: new Date(document.updated_at),
        lastAccessedAt: document.last_accessed_at ? new Date(document.last_accessed_at) : undefined,
        version: document.version,
        tags: document.tags,
        metadata: document.metadata
      },
      permissionLevel: 'owner'
    };
    
  } catch (error: any) {
    console.error('Failed to fetch secure document:', error);
    
    // Log failed access attempt
    await logDocumentAccess(documentId, 'view', 'denied', {
      error: error.message
    });
    
    return { error: error.message };
  }
};

// Download a document
export const downloadSecureDocument = async (documentId: string): Promise<{
  success: boolean;
  data?: ArrayBuffer;
  fileName?: string;
  fileType?: string;
  error?: string;
}> => {
  try {
    // Get document
    const { document, permissionLevel, error } = await getSecureDocument(documentId);
    
    if (error || !document) {
      throw new Error(error || 'Document not found');
    }
    
    // Check permission (owner or has at least read access)
    if (permissionLevel !== 'owner' && permissionLevel !== 'read' && 
        permissionLevel !== 'write' && permissionLevel !== 'admin') {
      throw new Error('You do not have permission to download this document');
    }
    
    // Get encryption key from localStorage
    const keyData = localStorage.getItem(`secure_doc_key_${documentId}`);
    if (!keyData) {
      throw new Error('Encryption key not found. You may need to request access again.');
    }
    
    const { key, iv } = JSON.parse(keyData);
    
    // Get document from storage
    const { data: encryptedData, error: storageError } = await supabase.storage
      .from('secure_files')
      .download(document.metadata.storagePath || '');
      
    if (storageError || !encryptedData) {
      throw new Error(`Failed to download encrypted file: ${storageError?.message || 'File not found'}`);
    }
    
    // Convert Blob to string
    const encryptedString = await encryptedData.text();
    
    // Decrypt data
    const decryptedData = await decryptFileData(encryptedString, key, iv);
    
    // Log successful download
    await logDocumentAccess(documentId, 'download', 'success');
    
    return {
      success: true,
      data: decryptedData,
      fileName: document.name,
      fileType: document.fileType
    };
    
  } catch (error: any) {
    console.error('Failed to download secure document:', error);
    
    // Log failed download attempt
    await logDocumentAccess(documentId, 'download', 'denied', {
      error: error.message
    });
    
    return { success: false, error: error.message };
  }
};

// Share a document with another user
export const shareDocument = async (
  documentId: string,
  userEmail: string,
  permissionLevel: 'read' | 'write' | 'admin',
  expiresAt?: Date
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Get CSRF token
    const { data: { token }, error: csrfError } = await supabase.rpc('generate_csrf_token');
    
    if (csrfError) {
      throw new Error(`Failed to generate CSRF token: ${csrfError.message}`);
    }
    
    // Find user by email
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('id')
      .eq('email', userEmail)
      .single();
      
    if (userError) {
      throw new Error(`User not found: ${userError.message}`);
    }
    
    // Share document
    const { data, error } = await supabase.rpc('share_document', {
      p_document_id: documentId,
      p_user_id: userData.id,
      p_permission_level: permissionLevel,
      p_expires_at: expiresAt?.toISOString(),
      p_csrf_token: token
    });
    
    if (error) {
      throw new Error(`Failed to share document: ${error.message}`);
    }
    
    // Log successful share
    await logDocumentAccess(documentId, 'share', 'success', {
      sharedWith: userEmail,
      permissionLevel
    });
    
    return { success: true };
    
  } catch (error: any) {
    console.error('Failed to share document:', error);
    
    // Log failed share attempt
    await logDocumentAccess(documentId, 'share', 'error', {
      error: error.message
    });
    
    return { success: false, error: error.message };
  }
};

// Revoke document access
export const revokeDocumentAccess = async (
  documentId: string,
  userId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Get CSRF token
    const { data: { token }, error: csrfError } = await supabase.rpc('generate_csrf_token');
    
    if (csrfError) {
      throw new Error(`Failed to generate CSRF token: ${csrfError.message}`);
    }
    
    // Revoke access
    const { data, error } = await supabase.rpc('revoke_document_access', {
      p_document_id: documentId,
      p_user_id: userId,
      p_csrf_token: token
    });
    
    if (error) {
      throw new Error(`Failed to revoke access: ${error.message}`);
    }
    
    return { success: true };
    
  } catch (error: any) {
    console.error('Failed to revoke document access:', error);
    return { success: false, error: error.message };
  }
};

// Get document permissions
export const getDocumentPermissions = async (documentId: string): Promise<{
  permissions?: DocumentPermission[];
  error?: string;
}> => {
  try {
    // Check if user has permission to view permissions
    const { document, permissionLevel, error } = await getSecureDocument(documentId);
    
    if (error || !document) {
      throw new Error(error || 'Document not found');
    }
    
    if (permissionLevel !== 'owner' && permissionLevel !== 'admin') {
      throw new Error('You do not have permission to view document permissions');
    }
    
    // Get permissions
    const { data, error: permissionsError } = await supabase
      .from('document_permissions')
      .select(`
        id,
        document_id,
        granted_by,
        granted_to,
        permission_level,
        granted_at,
        expires_at,
        revoked_at,
        revoked_by,
        users:granted_to (email, name),
        granters:granted_by (email, name),
        revokers:revoked_by (email, name)
      `)
      .eq('document_id', documentId);
      
    if (permissionsError) {
      throw new Error(`Failed to fetch permissions: ${permissionsError.message}`);
    }
    
    // Transform data
    const permissions = data.map(perm => ({
      id: perm.id,
      documentId: perm.document_id,
      grantedBy: perm.granted_by,
      grantedTo: perm.granted_to,
      permissionLevel: perm.permission_level as 'read' | 'write' | 'admin',
      grantedAt: new Date(perm.granted_at),
      expiresAt: perm.expires_at ? new Date(perm.expires_at) : undefined,
      revokedAt: perm.revoked_at ? new Date(perm.revoked_at) : undefined,
      revokedBy: perm.revoked_by,
      isActive: perm.revoked_at === null && (perm.expires_at === null || new Date(perm.expires_at) > new Date()),
      grantedToEmail: perm.users?.email,
      grantedToName: perm.users?.name,
      grantedByEmail: perm.granters?.email,
      grantedByName: perm.granters?.name,
      revokedByEmail: perm.revokers?.email,
      revokedByName: perm.revokers?.name
    }));
    
    return { permissions };
    
  } catch (error: any) {
    console.error('Failed to fetch document permissions:', error);
    return { error: error.message };
  }
};

// Log document access
export const logDocumentAccess = async (
  documentId: string,
  accessType: DocumentAccessLog['accessType'],
  accessStatus: DocumentAccessLog['accessStatus'],
  additionalInfo?: DocumentAccessLog['additionalInfo']
): Promise<void> => {
  try {
    await supabase.rpc('log_document_access', {
      p_document_id: documentId,
      p_access_type: accessType,
      p_access_status: accessStatus,
      p_additional_info: additionalInfo || {}
    });
  } catch (error) {
    console.error('Failed to log document access:', error);
    // Non-blocking - continue even if logging fails
  }
};

// Get document access logs
export const getDocumentAccessLogs = async (documentId: string): Promise<{
  logs?: any[];
  error?: string;
}> => {
  try {
    // Check if user has permission to view logs
    const { document, permissionLevel, error } = await getSecureDocument(documentId);
    
    if (error || !document) {
      throw new Error(error || 'Document not found');
    }
    
    if (permissionLevel !== 'owner' && permissionLevel !== 'admin') {
      throw new Error('You do not have permission to view document access logs');
    }
    
    // Get logs
    const { data, error: logsError } = await supabase
      .from('document_access_log')
      .select(`
        id,
        document_id,
        user_id,
        access_type,
        access_status,
        ip_address,
        user_agent,
        created_at,
        additional_info,
        users:user_id (email, name)
      `)
      .eq('document_id', documentId)
      .order('created_at', { ascending: false });
      
    if (logsError) {
      throw new Error(`Failed to fetch access logs: ${logsError.message}`);
    }
    
    return { logs: data };
    
  } catch (error: any) {
    console.error('Failed to fetch document access logs:', error);
    return { error: error.message };
  }
};

// Delete a document
export const deleteSecureDocument = async (documentId: string): Promise<{
  success: boolean;
  error?: string;
}> => {
  try {
    // Check if user has permission to delete
    const { document, permissionLevel, error } = await getSecureDocument(documentId);
    
    if (error || !document) {
      throw new Error(error || 'Document not found');
    }
    
    if (permissionLevel !== 'owner') {
      throw new Error('Only the document owner can delete this document');
    }
    
    // Get CSRF token
    const { data: { token }, error: csrfError } = await supabase.rpc('generate_csrf_token');
    
    if (csrfError) {
      throw new Error(`Failed to generate CSRF token: ${csrfError.message}`);
    }
    
    // Delete document
    const { error: deleteError } = await supabase
      .from('secure_documents')
      .delete()
      .eq('id', documentId)
      .eq('owner_id', (await supabase.auth.getUser()).data.user?.id);
      
    if (deleteError) {
      throw new Error(`Failed to delete document: ${deleteError.message}`);
    }
    
    // Delete file from storage
    if (document.metadata.storagePath) {
      await supabase.storage
        .from('secure_files')
        .remove([document.metadata.storagePath]);
    }
    
    // Remove encryption key from localStorage
    localStorage.removeItem(`secure_doc_key_${documentId}`);
    
    return { success: true };
    
  } catch (error: any) {
    console.error('Failed to delete secure document:', error);
    return { success: false, error: error.message };
  }
};